import { DateOnly } from '@if/core/date'
import { EFinancingProgram, EOriginSystemId } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { ControlledNumberField, ControlledSelect, ControlledDateOnlyPicker, FieldGrid, FieldsetCard } from '@if/mui'
import { CircularProgress, Stack } from '@mui/material'
import { useTermOptionList } from '@src/containers/ViewCreditApplicationPage/worksheet-hooks'
import { useGetFirstPaymentDateOptions } from '@src/data/api/credit-api/credit-api'
import { ComputeMechanicalWarrantyResultDto } from '@src/types/ComputeMechanicalWarrantyResultDto'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { MechanicalWarrantyProduct } from '@src/types/MechanicalWarrantyProduct'
import { MechanicalWarrantyWorksheet } from '@src/types/WorksheetSchema'
import { useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { usePaymentFrequencyOptions } from '../hooks/usePaymentFrequencyOptions'

interface Props {
  creditApplication: CreditApplication
  computedLoanAmounts: ComputeMechanicalWarrantyResultDto | null
  merchantMechanicalWarrantyProducts: MechanicalWarrantyProduct[]
  isComputing: boolean
}

const config = FinancingProgramConfigs[EFinancingProgram.MechanicalWarranty]

const MechanicalWarrantyWorksheetDetails = ({
  creditApplication,
  computedLoanAmounts,
  isComputing,
  merchantMechanicalWarrantyProducts,
}: Props) => {
  const { control, setValue } = useFormContext<MechanicalWarrantyWorksheet>()
  const { t } = useTranslation()

  const paymentFrequencyOptions = usePaymentFrequencyOptions({ creditApplication, config })

  const maxActivationDate = useMemo(() => new Date(creditApplication.expiresOn), [creditApplication.expiresOn])
  const today = useMemo(() => new Date(), [])

  const [deliveryOn, paymentFrequency, mechanicalWarrantyProductId] = useWatch({
    control,
    name: ['deliveryOn', 'paymentFrequency', 'mechanicalWarrantyProductId'],
  })

  const selectedProduct = useMemo(
    () => merchantMechanicalWarrantyProducts.find((product) => product.id === mechanicalWarrantyProductId),
    [merchantMechanicalWarrantyProducts, mechanicalWarrantyProductId],
  )

  const availableTerms = useMemo(() => {
    return selectedProduct
      ? config.termList.filter((term) => Number(term) <= Number(selectedProduct.duration))
      : config.termList
  }, [selectedProduct])

  const availableTermOptions = useTermOptionList(availableTerms)

  const firstPaymentDateOptions = useGetFirstPaymentDateOptions(
    creditApplication.id,
    creditApplication.financingProgramId,
    {
      deliveryOn: formatDate(deliveryOn),
      systemOriginId: EOriginSystemId.MerchantDashboard,
      isHighEstimate: false,
      paymentFrequency: paymentFrequency,
    },
  )

  const [minFirstPaymentDate, maxFirstPaymentDate] = useMemo(() => {
    return [
      new DateOnly(firstPaymentDateOptions?.[0]).toDate(),
      new DateOnly(firstPaymentDateOptions?.[firstPaymentDateOptions.length - 1]).toDate(),
    ]
  }, [firstPaymentDateOptions])

  // Set first payment date based on delivery date and payment frequency
  useEffect(() => {
    setValue('firstPaymentOn', new DateOnly(minFirstPaymentDate))
  }, [minFirstPaymentDate, setValue])

  // Update amount when computation completes
  useEffect(() => {
    if (computedLoanAmounts?.amountRequested != null) {
      setValue('amountRequested', computedLoanAmounts.amountRequested, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }, [computedLoanAmounts?.amountRequested, setValue])

  // Auto-set term to product duration when product changes
  useEffect(() => {
    if (selectedProduct?.duration) {
      setValue('term', selectedProduct.duration, { shouldDirty: true, shouldValidate: true })
    }
  }, [selectedProduct?.duration, setValue])

  return (
    <FieldsetCard title={t('common.financing')}>
      <FieldGrid columns={{ xs: 2, md: 3 }}>
        {isComputing ? (
          <Stack direction="row" justifyContent="left" alignItems="center" sx={{ minHeight: 40 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <ControlledNumberField
            control={control}
            name="amountRequested"
            label={t('worksheet.requestedAmount')}
            disabled
            fullWidth
            isMoney
          />
        )}
        <ControlledSelect
          label={t('worksheet.paymentFrequency')}
          control={control}
          name="paymentFrequency"
          options={paymentFrequencyOptions}
          disableEmptyOption
          fullWidth
        />
        <ControlledSelect
          label={t('worksheet.loanTerm')}
          name="term"
          control={control}
          options={availableTermOptions}
          disableEmptyOption
          fullWidth
          disabled
        />
        <ControlledDateOnlyPicker
          name="deliveryOn"
          control={control}
          label={t('worksheet.activationDate')}
          disablePast
          minDate={today}
          maxDate={maxActivationDate}
          fullWidth
        />
        <ControlledDateOnlyPicker
          name="firstPaymentOn"
          control={control}
          label={t('worksheet.firstPaymentOn')}
          disablePast
          minDate={minFirstPaymentDate}
          maxDate={maxFirstPaymentDate}
          disabled={!paymentFrequency}
          fullWidth
        />
      </FieldGrid>
    </FieldsetCard>
  )
}

export default MechanicalWarrantyWorksheetDetails
